import { ComponentType } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { filterNil, filterUndefined } from '@principle-theorem/shared';
import { AuthService } from '../auth.service';
import { AuthSignInErrorHandlerService } from '../auth-sign-in-error-handler.service';
import { IWorkspaceSelector } from '../workspace-selector/workspace-selector.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { AuthProcessService, AuthProvider } from '../auth-process.service';
import { Layout } from '../auth-interfaces';
import { NgxAuthFirebaseuiLoginComponent } from '../ngx-auth-firebaseui-login/ngx-auth-firebaseui-login.component';

@Component({
  selector: 'pt-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AuthSignInErrorHandlerService],
})
export class SignInComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  private _authProcess = inject(AuthProcessService);
  private _errorHandler = inject(AuthSignInErrorHandlerService);
  auth = inject(AuthService);
  providers: AuthProvider[] = [AuthProvider.Google, AuthProvider.Facebook];
  providersLayout: Layout = Layout.COLUMN;
  userIsVerified$: Observable<boolean>;
  resetPassword$ = new BehaviorSubject<boolean>(false);
  emailCtrl = new TypedFormControl<string>('', [
    Validators.email,
    Validators.required,
  ]);
  resetPasswordForm = new TypedFormGroup({
    email: this.emailCtrl,
  });
  loginComponent$ = new ReplaySubject<NgxAuthFirebaseuiLoginComponent>(1);
  logoSrc$ = new ReplaySubject<string>(1);

  @ViewChild('loginComponent')
  set registerComponent(component: NgxAuthFirebaseuiLoginComponent) {
    if (component) {
      this.loginComponent$.next(component);
    }
  }
  @Input() workspaceSelectors: ComponentType<IWorkspaceSelector>[];
  /**
   * Turn off in apps without workspaces, which navigate on `afterSuccess`
   * themselves. The workspace selector loads the user's workspaces and, when
   * there is exactly one, selects it and navigates on its own.
   */
  readonly selectWorkspace = input(true);

  @Input()
  set logoSrc(src: string) {
    if (src) {
      this.logoSrc$.next(src);
    }
  }

  @Output() afterSuccess: EventEmitter<void> = new EventEmitter<void>();
  @Output() afterError: EventEmitter<void> = new EventEmitter<void>();

  constructor() {
    this.userIsVerified$ = this.auth.authUser$.pipe(
      filterUndefined(),
      map((user) => user.emailVerified),
      startWith(false)
    );

    this.loginComponent$
      .pipe(
        map(
          (component) =>
            component.loginForm.get('email') as AbstractControl<string> | null
        ),
        filterNil(),
        switchMap((emailCtrl) =>
          emailCtrl.valueChanges.pipe(
            debounceTime(250),
            map((value) => ({ value, emailCtrl }))
          )
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe(({ value, emailCtrl }) =>
        emailCtrl.setValue(value.toLowerCase())
      );
  }

  async onSuccess(): Promise<void> {
    await this.auth.updateClaims();
    this.afterSuccess.emit();
  }

  async onError(event: Error): Promise<void> {
    const result = await this._errorHandler.handle(event);

    if (result === 'handled') {
      return;
    }

    this.afterError.emit();
  }

  async onCreateAccount(): Promise<void> {
    await this._router.navigate(['register']);
  }

  async resetPassword(): Promise<void> {
    if (!this.resetPasswordForm.valid) {
      return;
    }
    try {
      await this._authProcess.resetPassword(this.emailCtrl.value);
      this._snackBar.open(
        `Please follow the link sent to ${this.emailCtrl.value}`
      );
    } catch {
      this._snackBar.open('Error sending reset link. Please try again.');
    }
    this.resetPassword$.next(false);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
